import { Twitter, Github, Send } from "lucide-react";
import { Logo } from "@/components/Logo";
import { scrollToSection } from "@/components/Reveal";

const COLS = [
    {
        title: "Product",
        links: [
            { label: "Core pillars", href: "#pillars" },
            { label: "How it works", href: "#how" },
            { label: "Waitlist", href: "#waitlist" },
        ],
    },
    {
        title: "Ecosystem",
        links: [
            { label: "Caldera", href: "#" },
            { label: "Somnia", href: "#" },
            { label: "Theoriq", href: "#" },
            { label: "Union", href: "#" },
            { label: "Irys", href: "#" },
            { label: "Rayls", href: "#" },
        ],
    },
    {
        title: "Legal",
        links: [
            { label: "Privacy policy", href: "#" },
            { label: "Terms of service", href: "#" },
        ],
    },
];

const SOCIALS = [
    { icon: Twitter, label: "X / Twitter", testid: "footer-social-x" },
    { icon: Github, label: "GitHub", testid: "footer-social-github" },
    { icon: Send, label: "Telegram", testid: "footer-social-telegram" },
];

export default function Footer() {
    const year = new Date().getFullYear();
    return (
        <footer className="relative overflow-hidden border-t border-white/10 bg-[#020305] px-4 pb-10 pt-16 sm:px-6 lg:px-8">
            <div
                aria-hidden="true"
                className="pointer-events-none absolute -bottom-[4vw] left-1/2 -translate-x-1/2 select-none whitespace-nowrap font-display text-[20vw] font-extrabold leading-none text-white/[0.03]"
            >
                AUTHENA
            </div>

            <div className="relative mx-auto max-w-7xl">
                <div className="grid grid-cols-1 gap-10 md:grid-cols-12">
                    <div className="md:col-span-5">
                        <Logo id="footer" />
                        <p className="mt-5 max-w-sm text-sm leading-relaxed text-slate-400">
                            Trust infrastructure for autonomous agents — chain-agnostic identity bound to
                            verified humans, with cryptographic execution proofs on every action.
                        </p>
                        <div className="mt-6 flex gap-3">
                            {SOCIALS.map((s) => (
                                <a
                                    key={s.label}
                                    href="#"
                                    aria-label={s.label}
                                    data-testid={s.testid}
                                    className="flex h-10 w-10 items-center justify-center rounded-lg border border-white/10 text-slate-400 transition-all duration-300 hover:border-neon-cyan/40 hover:text-neon-cyan"
                                >
                                    <s.icon size={16} />
                                </a>
                            ))}
                        </div>
                    </div>

                    {COLS.map((col) => (
                        <div key={col.title} className="md:col-span-2" data-testid={`footer-col-${col.title.toLowerCase()}`}>
                            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                                {col.title}
                            </p>
                            <ul className="mt-4 space-y-2.5">
                                {col.links.map((l) => (
                                    <li key={l.label}>
                                        <a
                                            href={l.href}
                                            onClick={l.href.startsWith("#") && l.href !== "#" ? (e) => { e.preventDefault(); scrollToSection(l.href); } : undefined}
                                            className="text-sm text-slate-400 transition-colors hover:text-neon-cyan"
                                        >
                                            {l.label}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}

                    <div className="md:col-span-1">
                        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">Status</p>
                        <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-neon-emerald/25 bg-neon-emerald/5 px-3 py-1.5">
                            <span className="pulse-dot h-1.5 w-1.5 rounded-full bg-neon-emerald" />
                            <span className="font-mono text-[9px] uppercase tracking-[0.16em] text-neon-emerald">
                                Operational
                            </span>
                        </div>
                    </div>
                </div>

                <div className="mt-14 flex flex-col items-center justify-between gap-3 border-t border-white/5 pt-6 sm:flex-row">
                    <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-600">
                        © {year} Authena.xyz — All rights reserved
                    </p>
                    <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-600">
                        Identity for humans · Proof for agents
                    </p>
                </div>
            </div>
        </footer>
    );
}